import { completeRegSectionReview, getRegSection, searchRegsV2 } from "./api.js";
import { computed, reactive } from "vue";

export const LATEST_AMENDMENT = 27;
export const ALL_TYPES = ["cs", "amc", "gm", "appendix"];

const PAGE_SIZE = 30;
const state = reactive({
    partNumber: "25",
    amendmentNumber: LATEST_AMENDMENT,
    activeTypes: ["cs", "amc"],
    searchQuery: "",
    searchResults: [],
    searchTotal: 0,
    searchLoading: false,
    searchError: "",
    searchPage: 0,
    currentSection: null,
    sectionLoading: false,
    sectionError: "",
    selectedVersionId: null,
});

let searchRequestId = 0;
let sectionRequestId = 0;
let selectedAmendmentNumber = null;
const hasMore = computed(() => state.searchResults.length < state.searchTotal);

async function search(reset = true) {
    
    
    const requestId = ++searchRequestId;
    if (reset) {
        state.searchPage = 0;
        state.searchResults = [];
    }
    state.searchLoading = true;
    state.searchError = "";
    try {
        const { data } = await searchRegsV2({
            query: state.searchQuery,
            authority: "EASA",
            partNumber: state.partNumber,
            amendmentNumber: state.amendmentNumber,
            types: [...state.activeTypes],
            size: PAGE_SIZE,
            from: state.searchPage * PAGE_SIZE,
        });
        
        if (requestId !== searchRequestId) return;
        state.searchResults = reset
            ? (data.hits || [])
            : [...state.searchResults, ...(data.hits || [])];
        state.searchTotal = data.total || 0;
    } catch (error) {
        if (requestId !== searchRequestId) return;
        console.error("[Regulations] search failed", error);
        state.searchError = error?.message || "Unable to search regulations.";
    } finally {
        if (requestId === searchRequestId) state.searchLoading = false;
    }
}

async function loadMore() {
    if (state.searchLoading || !hasMore.value) return;
    state.searchPage++;
    await search(false);
}

async function selectSection(versionId) {
    if (
        state.selectedVersionId === versionId
        && selectedAmendmentNumber === state.amendmentNumber
        && state.currentSection
    ) return;

    const requestId = ++sectionRequestId;
    state.selectedVersionId = versionId;
    state.currentSection = null;
    state.sectionError = "";
    state.sectionLoading = true;
    try {
        const { data } = await getRegSection({
            versionId,
            amendmentNumber: state.amendmentNumber,
        });
        if (requestId !== sectionRequestId) return;
        state.currentSection = data;
        selectedAmendmentNumber = state.amendmentNumber;
    } catch (error) {
        if (requestId !== sectionRequestId) return;
        console.error("[Regulations] section load failed", error);
        state.sectionError = error?.message || "Unable to load this regulation section.";
    } finally {
        if (requestId === sectionRequestId) state.sectionLoading = false;
    }
}

function clearSection() {
    sectionRequestId++;
    state.selectedVersionId = null;
    selectedAmendmentNumber = null;
    state.currentSection = null;
    state.sectionError = "";
    state.sectionLoading = false;
}

async function completeReviewIssue(reason, note = "") {
    if (!state.selectedVersionId || !state.currentSection) {
        throw new Error("No regulation section is selected.");
    }
    const { data } = await completeRegSectionReview({
        versionId: state.selectedVersionId,
        reason,
        note,
    });
    state.currentSection = { ...state.currentSection, ...data };
}

function replaceParagraph(updatedParagraph) {
    if (!state.currentSection || !updatedParagraph?.paragraphId) return;
    state.currentSection = {
        ...state.currentSection,
        paragraphs: (state.currentSection.paragraphs || []).map(paragraph => (
            paragraph.paragraphId === updatedParagraph.paragraphId
                ? { ...paragraph, ...updatedParagraph }
                : paragraph
        )),
    };
}

function setAmendment(amendment) {
    state.amendmentNumber = Number(amendment);
}

function setPartNumber(partNumber) {
    state.partNumber = String(partNumber);
}

function setQuery(query) {
    state.searchQuery = String(query || "");
    return search();
}

function toggleType(type) {
    state.activeTypes = state.activeTypes.includes(type)
        ? state.activeTypes.filter((value) => value !== type)
        : [...state.activeTypes, type];
    return search();
}

export function useRegulationsState() {
    return {
        state,
        hasMore,
        search,
        loadMore,
        selectSection,
        clearSection,
        completeReviewIssue,
        replaceParagraph,
        setAmendment,
        setPartNumber,
        setQuery,
        toggleType,
    };
}
